/**
 * Authentication utility functions for the Twitter Growth Tracker platform
 */

/**
 * Get the authentication token from local storage
 * @returns {string|null} The authentication token or null if not found
 */
export const getAuthToken = () => {
  try {
    // Try to get token from localStorage
    // Check both 'auth_token' (used by authUtils) and 'token' (used by AuthContext)
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');

    if (token) {
      return token;
    }

    // If no token in localStorage, try to get from sessionStorage
    const sessionToken = sessionStorage.getItem('auth_token') || sessionStorage.getItem('token');

    if (sessionToken) {
      return sessionToken;
    }

    // No token found
    return null;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

/**
 * Set the authentication token in storage
 * @param {string} token - The authentication token
 * @param {boolean} rememberMe - Whether to store in localStorage (true) or sessionStorage (false)
 */
export const setAuthToken = (token, rememberMe = false) => {
  try {
    if (rememberMe) {
      localStorage.setItem('auth_token', token);
      // Clean up session storage to avoid confusion
      sessionStorage.removeItem('auth_token');
    } else {
      sessionStorage.setItem('auth_token', token);
      // Clean up local storage to avoid confusion
      localStorage.removeItem('auth_token');
    }
  } catch (error) {
    console.error('Error setting auth token:', error);
  }
};

/**
 * Remove the authentication token from storage
 */
export const removeAuthToken = () => {
  try {
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('auth_token');
  } catch (error) {
    console.error('Error removing auth token:', error);
  }
};

/**
 * Check if the user is authenticated
 * @returns {boolean} Whether the user is authenticated
 */
export const isAuthenticated = () => {
  return !!getAuthToken();
};

/**
 * Parse a JWT token to get the payload
 * @param {string} token - The JWT token
 * @returns {Object|null} The payload or null if invalid
 */
export const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT:', error);
    return null;
  }
};

/**
 * Check if the token is expired
 * @param {string} token - The JWT token
 * @returns {boolean} Whether the token is expired
 */
export const isTokenExpired = (token) => {
  try {
    const decoded = parseJwt(token);
    if (!decoded || !decoded.exp) {
      return true;
    }
    
    // Check if token is expired (exp is in seconds, Date.now() is in milliseconds)
    return decoded.exp * 1000 < Date.now();
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};
